// Deliberately dependency-free (no SDK, no RTK, no MUI) so
// `notabeneWidgetAsset.test.ts` can pin this logic without booting the full
// NotabeneHostedMount component — same reason as notabeneAgentSections.ts.

/**
 * Why the SafeConnect mounts pass a CAIP-19 instead of our ticker.
 *
 * The hosted widget (connect.notabene.id) validates the RAW `value.asset` the
 * host hands it against `NotabeneAsset | CAIP-19 | DTI`, where `NotabeneAsset`
 * is `^\$?[A-Z0-9-]{2,20}$`. A one-character ticker (`W`, Wormhole) fails that
 * schema, and the widget's only reaction is to keep its Complete button hidden
 * while every section still ticks green — the customer sees a finished form
 * with no Submit (prod 2026-11-04). The widget resolves a CAIP-19 on OUR chain,
 * which is also why the emailed manual link already carries one
 * (`ManualTrService`, prod 2026-08-12 AXL chain mismatch).
 *
 * Resolution is best-effort: on a mapping miss we fall back to the ticker,
 * which is today's behaviour for every multi-character asset.
 */

/** Mirror of the widget's `NotabeneAsset` ticker schema. */
export const NOTABENE_TICKER_RE = /^(\$?[A-Z0-9-]{2,20})$/;

/**
 * True when the value already carries a chain — a CAIP-19
 * (`solana:…/token:…`, `eip155:1/slip44:60`) — and must be passed through
 * untouched. Call sites that already hold one (transfer detail pages) skip the
 * lookup entirely.
 */
export const isCaip19Like = (value: string | undefined | null): boolean =>
  typeof value === "string" && /^[-a-z0-9]{3,8}:[^/]+\/[^/]+$/i.test(value);

export type WidgetAssetLookup = {
  /** The lookup has settled — success, 404 or network error. */
  settled: boolean;
  /** The resolved CAIP-19 when the lookup succeeded. */
  caip19?: string | null;
};

export type WidgetAssetResolution = {
  /** What to hand the SDK as `asset` — `undefined` while still resolving. */
  asset: string | undefined;
  /** True while a lookup is still needed and hasn't settled. */
  pending: boolean;
  /** Whether a lookup should be issued at all for this input. */
  needsLookup: boolean;
};

/**
 * Decide what the SDK receives as `asset`.
 *
 *  - no asset            → nothing to resolve (the kind-specific guard throws)
 *  - already a CAIP-19   → pass through, no lookup
 *  - ticker              → wait for the lookup; CAIP-19 on hit, ticker on miss
 */
export const resolveWidgetAsset = (
  asset: string | undefined,
  lookup: WidgetAssetLookup,
): WidgetAssetResolution => {
  if (!asset) return { asset: undefined, pending: false, needsLookup: false };
  if (isCaip19Like(asset)) return { asset, pending: false, needsLookup: false };
  if (!lookup.settled) return { asset: undefined, pending: true, needsLookup: true };
  return { asset: lookup.caip19 || asset, pending: false, needsLookup: true };
};
